import axios from "axios";
import { bootstrapClientToken, getClientToken } from "@/server/oauthService";

// Instância personalizada do axios
const instance = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: 20000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

let isRefreshing = false;   // evita múltiplas requisições de refresh simultâneas
let failedQueue = [];       // fila de requisições aguardando o refresh

const processQueue = (error, token = null) => {
  failedQueue.forEach((promise) => {
    if (token) {
      promise.resolve(token);
    } else {
      promise.reject(error);
    }
  });
  failedQueue = [];
};

// ---------------------------------------------------------------------------
// Request interceptor — injeta o token correto
//   - Usuário logado  → access_token do Sanctum (localStorage['auth'])
//   - Visitante       → access_token do cliente OAuth (localStorage['oauth_client'])
// ---------------------------------------------------------------------------
instance.interceptors.request.use(
  (config) => {
    const auth = JSON.parse(localStorage.getItem("auth") || "null");

    if (auth?.access_token) {
      config.headers["Authorization"] = `Bearer ${auth.access_token}`;
    } else {
      const oauthClient = getClientToken();
      if (oauthClient?.access_token) {
        config.headers["Authorization"] = `Bearer ${oauthClient.access_token}`;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ---------------------------------------------------------------------------
// Response interceptor — trata 401 com refresh automático
// ---------------------------------------------------------------------------
instance.interceptors.response.use(
  (response) => response.data, // unwrap para todos os serviços receberem body direto

  async (error) => {
    const status = error.response?.status ?? null;

    if (status !== 401) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    if (originalRequest._retry) {
      return Promise.reject(error);
    }
    originalRequest._retry = true;

    const auth = JSON.parse(localStorage.getItem("auth") || "null");
    const isLoggedIn = !!auth?.refresh_token;

    // -----------------------------------------------------------------------
    // Usuário logado — tenta renovar o par de tokens com o refresh_token
    // -----------------------------------------------------------------------
    if (isLoggedIn) {
      if (!isRefreshing) {
        isRefreshing = true;

        try {
          // Usa axios nativo (sem unwrap) pois a instância já está no interceptor
          const response = await axios.post(
            process.env.VUE_APP_API_URL + "api/refresh-token",
            {},
            {
              headers: {
                Authorization: `Bearer ${auth.refresh_token}`,
                "Content-Type": "application/json",
                Accept: "application/json",
              },
            }
          );

          // response.data = { success, message, data: { auth, user, permissions } }
          const body    = response.data;
          const newAuth = body.data.auth;

          localStorage.setItem("auth", JSON.stringify(newAuth));
          if (body.data?.user)        localStorage.setItem("user", JSON.stringify(body.data.user));
          if (body.data?.permissions) localStorage.setItem("permissions", JSON.stringify(body.data.permissions));

          processQueue(null, newAuth.access_token);
          isRefreshing = false;

          originalRequest.headers["Authorization"] = `Bearer ${newAuth.access_token}`;
          return instance(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          isRefreshing = false;
          // Refresh falhou — limpa sessão e redireciona para login
          localStorage.removeItem("auth");
          localStorage.removeItem("user");
          localStorage.removeItem("permissions");
          window.location.href = "/#/login";
          return Promise.reject(refreshError);
        }
      }

      // Outro request chegou enquanto refresh ainda está em andamento — enfileira
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            resolve(instance(originalRequest));
          },
          reject: (err) => reject(err),
        });
      });
    }

    // -----------------------------------------------------------------------
    // Visitante — token de cliente expirou ou inválido → re-bootstrap
    // -----------------------------------------------------------------------
    if (!isRefreshing) {
      isRefreshing = true;

      try {
        const newClientToken = await bootstrapClientToken();
        processQueue(null, newClientToken?.access_token ?? null);
        isRefreshing = false;

        if (newClientToken?.access_token) {
          originalRequest.headers["Authorization"] = `Bearer ${newClientToken.access_token}`;
          return instance(originalRequest);
        }
      } catch (bootstrapError) {
        processQueue(bootstrapError, null);
        isRefreshing = false;
        return Promise.reject(bootstrapError);
      }
    }

    return new Promise((resolve, reject) => {
      failedQueue.push({
        resolve: (token) => {
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          resolve(instance(originalRequest));
        },
        reject: (err) => reject(err),
      });
    });
  }
);

export default instance;

